import { Component, OnInit, AfterViewChecked, OnDestroy } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { Router, NavigationStart, NavigationEnd, NavigationCancel } from '@angular/router';
import { LoadingController } from './_shared/modules/loader/loader-controller.service';
import { ToastrService, GlobalConfig } from 'ngx-toastr';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { SocketConnections, SvgIconPathMap } from 'sharedweb/app.structs';
import * as Feather from 'feather-icons';
import { EventsService, SocketService } from './_shared/providers';
import { IONIC_EVENTS } from 'sharedweb/helpers/shared-constants';
import { load as fingerPrintLoad } from 'src/assets/js/v3.js';
import { EVENTS, NET_STATUS_DELAY, SOCKET_INACTIVITY_TIMER, Utils } from './_helpers';
import { SymbolSearchService } from 'sharedweb/providers/symbol-search/symbol-search.service';
import { ConnectivityService } from './_shared/providers/connectivity/connectivity-service.service';
import { AUTH_NUDGE_PRIORITY, DisconnectionNudgeType, HEADER_NUDGE_CONFIGURATION, NudgeItemConfiguration, SERVER_NUDGE_PRIORITY } from './_shared/components/header-nudge/nudge-conf';
import { ConnectivityMap } from './_shared/providers/connectivity/connectivity.interface';
import { BOD_TIME_MSG } from 'sharedweb/strings/nudge.strings';
import { ConfigurationService } from './_shared/providers/configuration/configuration.service';
import { Subscription } from 'rxjs';
import { InactivityService } from 'sharedweb/providers/inactivity/inactivity.service';
import { throttleTime } from 'rxjs/operators';
import { InternetConnectivityService } from './_shared/providers/internet-connectivity/internet-connectivity.service';



@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss']
})

export class AppComponent implements OnInit, AfterViewChecked, OnDestroy {

    // use this to set correct theme class on app holder
    // eg: <div [class]="themeClass">...</div>
    themeClass: string;
    showConnectivityPopup = false;
    showNetConnectedPopup = false;
    nudgeConfiguration: NudgeItemConfiguration = null;
    netNudgeConfiguration: NudgeItemConfiguration = HEADER_NUDGE_CONFIGURATION[DisconnectionNudgeType.internetDisconnected];
    private inactivitySubscriptions = new Subscription();
    isAuthenticated = Utils.isAuthenticated();
    serverDisconnection: Subscription = new Subscription();
    isInactive = false;
    isLoading = false;

    constructor(
        private overlayContainer: OverlayContainer,
        private _router: Router,
        private _loader: LoadingController,
        private _toast: ToastrService,
        private _socket: SocketService,
        private _matIconRegistry: MatIconRegistry,
        private _domSanitizer: DomSanitizer,
        private eventsService: EventsService,
        // Need to initialize symbol search service at app startup, so imported here.
        private symbolSearchService: SymbolSearchService,
        private connectivityService: ConnectivityService,
        private remoteConfigService: ConfigurationService,
        private _inactivityService: InactivityService,
        private internetConnectivityService: InternetConnectivityService,
    ) {

        this.eventsService.subscribe(IONIC_EVENTS.showToast, (msg) => {
            this._toast.success(msg);
        });

        this.eventsService.subscribe(EVENTS.CHART_LOADED, (isLoaded:boolean) => {
            this.isLoading = !isLoaded;
        });

        this.eventsService.subscribe(EVENTS.LOGGED_IN, () => {
            this.serverDisconnection?.unsubscribe();
            this.inactivitySubscriptions.unsubscribe();
            this._inactivityService.clearResources();
            this.showConnectivityPopup = false;
            this.showNetConnectedPopup = false;
        });
        /**
         * Adding .svg icons to mat-icons
         */
        Object.keys(SvgIconPathMap).forEach(icon => {
            this._matIconRegistry.addSvgIcon(
                icon,
                this._domSanitizer.bypassSecurityTrustResourceUrl(SvgIconPathMap[icon])
            );
        });

        this.remoteConfigService.getEnvironmentFile().then(v => {
            const override: Partial<GlobalConfig> = {
                timeOut: (+v['TOAST_TIME']) * 1000
            };
            this._toast.toastrConfig = { ...this._toast.toastrConfig, ...override };
        }).catch(e => console.log('ERROR IN CONFIG', e));

        this._router.events.subscribe(event => {

            if(event instanceof NavigationStart && event.url.includes('chart')){
                this.isLoading = true;
            }

            /**
             * Ovgerride Transitions
             */
            if (event instanceof NavigationStart) {
                event.url.indexOf('/oauth/grant') < 0 && this._loader.show();
            }
            if (event instanceof NavigationEnd || event instanceof NavigationCancel) {
                // Utils.scrollToTop();
                this._loader.hide();
            }
        });

        fingerPrintLoad().then((fp) => fp.get())
            .then((result) => {
                // This is the visitor identifier:
                const visitorId = result.visitorId;
                const deviceID = Utils.storageHelper.deviceId;
                if (!deviceID) {
                    Utils.storageHelper.deviceId = visitorId;
                }
            })
            .catch((error) => {
                console.log(error);
            });
    }

    ngAfterViewChecked(): void {
        Feather.replace();
    }

    ngOnInit(): void {
        this.themeClass = 'utrade-plus-theme';
        this.overlayContainer.getContainerElement().classList.add(this.themeClass);
        // this.internetConnectivityService.initSubscriptions();
        // this.connectivityService.setAlwaysAliveSubscriptions();

        // future purpose
        // this._cacheService.getUserTheme().subscribe(cache => {
        //     if (cache) {
        //         this._themesService.changeTheme(cache);
        //     } else {
        //         this._themesService.changeTheme(Themes.White);
        //     }

        //     Utils.storageHelper.themeSelected = cache || Themes.White;
        // })


        // Commenting for now if not autheticated not trying to connect to socket.
        // if (!this.isAuthenticated) {
        // this.initInactivityLogout();
        // this.connectAndSubscribeToSocket();
        // }
    }

    initInactivityLogout() {

        this._inactivityService.startTimer(SOCKET_INACTIVITY_TIMER);

        this.inactivitySubscriptions.add(
            this._inactivityService.timeoutEvent.subscribe(isInactive => {
                if (isInactive) {
                    console.log(`User is inactive`);
                    if (this._socket.checkSocketConnected(SocketConnections.order)) {
                        this.disconnectAndUnSubscribeToSocket();
                        this.isInactive = true;
                    }
                }
            })
        );

        this.inactivitySubscriptions.add(
            this._inactivityService.events.pipe(
                throttleTime(1000)
            )
                .subscribe((e) => {
                    if (!this._socket.checkSocketConnected(SocketConnections.order)) {
                        this.connectAndSubscribeToSocket();
                        setTimeout(() => {
                            this.isInactive = false;
                        }, 1000);
                    }
                })
        );
    }

    ngOnDestroy(): void {
    }

    connectAndSubscribeToSocket() {
        this.initConnectivitySubscriptions();
        this._socket.connect(SocketConnections.order);
    }

    disconnectAndUnSubscribeToSocket() {
        this._socket.disconnectAllSockets();
        this.serverDisconnection.unsubscribe();
    }

    initConnectivitySubscriptions() {
        const nudgesEnabled = this.remoteConfigService.remoteConfigCacheObj?.HeaderNudges;
        // If nudges are enabled, then only subscribe to connectivity snapshots and store the subscription.
        if (nudgesEnabled) {
            this.serverDisconnection.add(this.connectivityService.connectivitySnapshots.subscribe({
                next: (connectivityMap: ConnectivityMap) => {
                    // If internet is disconnected, no need to process server disconnections.
                    for (const key in AUTH_NUDGE_PRIORITY) {
                        const serverConnection = AUTH_NUDGE_PRIORITY[key];
                        if (connectivityMap[serverConnection]) {
                            this.showConnectivityPopup = true;
                            this.nudgeConfiguration = HEADER_NUDGE_CONFIGURATION[serverConnection];

                            if (this.nudgeConfiguration.type === DisconnectionNudgeType.isUnderBODTime) {
                                this.nudgeConfiguration.title = BOD_TIME_MSG(this.remoteConfigService['remoteConfigCacheObj']['BodEndTime']);
                            }
                            return;
                        }
                    }
                    this.showConnectivityPopup = false;
                }
            }));

            this.serverDisconnection.add(this.internetConnectivityService.internetConnecitivity.subscribe({
                next: (isInternetConnected: boolean) => {
                    let delay = 0;
                    if (this.showNetConnectedPopup && isInternetConnected) {
                        delay = NET_STATUS_DELAY;
                    }
                    // We are delaying internet reconnectivity update by 5 sec(discussed with BA) as when internet is connected, web server disconnection shows up.
                    // Refer RE-1225
                    setTimeout(() => {
                        this.showNetConnectedPopup = !isInternetConnected;
                    }, delay);
                }
            }));
        }
    }
}
